import React from 'react'
import classnames from 'classnames'
import { Box, Grid, GridItem } from '@deal/components'
import { Link } from 'react-router-dom'
import {
  NavActiveDepartmentFragment,
  NavInactiveDepartmentFragment,
  DepartmentNavigationResponsiveSmallSizeOptions,
  DepartmentNavigationResponsiveFullSizeOptions
} from '#src/generated/graphql'
import DepartmentNavigationLink from '../../../common/DepartmentNavigationLink'
import DesktopTertiaryNav from '../../desktop/DesktopTertiaryNav'
import SearchIcon from './search.svg'
import { generateCreateUrlFromCategory } from '#src/services/path'
import styles from './styles.css'

interface SecondaryNavProps {
  departments?: NavInactiveDepartmentFragment[]
  activeDepartment?: NavActiveDepartmentFragment
  onBrowseClicked: React.MouseEventHandler
  onSearchClicked: React.MouseEventHandler
  transparent?: boolean
  replaceSearchWithPath?: boolean
}

interface SecondaryNavState {
  disabledCategories: Set<string>
}

/**
 * The secondary nav is the bar below the primary nav in the header.
 *
 * Its content is related to the active department:
 *   - Viewing the department's experts
 *   - Browsing the department's categories
 *   - Searching the department's sellables
 */
class SecondaryNav extends React.PureComponent<SecondaryNavProps, SecondaryNavState> {
  public state = {
    disabledCategories: new Set<string>()
  }

  /**
   * Temporarily mark a category a "unhoverable".
   *
   * This is necessary to dismiss the BrowseCategoryMenu after a link is clicked.
   */
  public temporarilyDisableHoverState(categoryId: string) {
    this.setState(
      prevState => {
        const disabledCategories = new Set(prevState.disabledCategories)
        disabledCategories.add(categoryId)
        return { disabledCategories }
      },
      () => {
        setTimeout(() => {
          this.setState(prevState => {
            const disabledCategories = new Set(prevState.disabledCategories)
            disabledCategories.delete(categoryId)
            return {
              disabledCategories
            }
          })
        }, 500)
      }
    )
  }

  public render() {
    const { activeDepartment, onBrowseClicked, departments, replaceSearchWithPath } = this.props

    const items = (activeDepartment && activeDepartment.navigation.topLevelItems) || []
    const desktopItems = items.filter(
      item =>
        item.showIfFullSize &&
        item.fullSizeOptions.find(
          option => option === DepartmentNavigationResponsiveFullSizeOptions.SHOW
        )
    )
    const mobileItems = items.filter(
      item =>
        item.showIfSmallSize &&
        item.smallSizeOptions.find(
          option =>
            option === DepartmentNavigationResponsiveSmallSizeOptions.SHOW_IN_PERSISTENT_HEADER
        )
    )

    return (
      <Grid>
        <GridItem col={2}>
          <nav
            className={classnames(styles.secondaryNav, {
              [styles.secondaryNavTransparent]: this.props.transparent
            })}
          >
            {activeDepartment ? (
              <Box spacing="none" items="stretch" className={styles.container}>
                <Box.Item grow={1}>
                  <ul className={styles.navList}>
                    {/* Desktop items */}
                    {desktopItems.map(item => {
                      return (
                        <li
                          className={classnames(styles.desktopNavItem, {
                            [styles.unhoverable]: this.state.disabledCategories.has(item.id)
                          })}
                          key={item.id}
                        >
                          {item.labelLink ? (
                            <DepartmentNavigationLink
                              link={item.labelLink}
                              className={styles.navItemLink}
                              activeClassName={styles.navItemLinkActive}
                            >
                              {item.label}
                            </DepartmentNavigationLink>
                          ) : (
                            <span>{item.label}</span>
                          )}

                          {item.subSections.length > 0 && (
                            <DesktopTertiaryNav
                              item={item}
                              className={styles.categoryMenu}
                              onLinkClick={this.temporarilyDisableHoverState.bind(this, item.id)}
                            />
                          )}
                        </li>
                      )
                    })}

                    {/* Mobile items (persistent items only: others are in the flyout menu) */}
                    {mobileItems.map(item => {
                      return (
                        <li className={styles.mobileNavItem} key={item.id}>
                          {item.labelLink ? (
                            <DepartmentNavigationLink
                              link={item.labelLink}
                              className={styles.navItemLink}
                              activeClassName={styles.navItemLinkActive}
                            >
                              {item.label}
                            </DepartmentNavigationLink>
                          ) : (
                            <span>{item.label}</span>
                          )}
                        </li>
                      )
                    })}

                    {/* "Browse" opens the mobile flyout menu */}
                    {items.length > 0 && (
                      <li className={styles.mobileNavItem}>
                        <a className={styles.navItemLink} onClick={onBrowseClicked}>
                          Browse
                        </a>
                      </li>
                    )}
                  </ul>
                </Box.Item>
                <Box.Item className={styles.searchContainer}>
                  {replaceSearchWithPath && activeDepartment ? (
                    <Link
                      to={generateCreateUrlFromCategory(
                        activeDepartment.topLevelCategories[0].slug
                      )}
                    >
                      <div
                        className={classnames(styles.pathButton, {
                          [styles.searchTransparent]: this.props.transparent
                        })}
                      >
                        <span className={styles.pathLabel}>Create new request</span>
                      </div>
                    </Link>
                  ) : (
                    <div
                      className={classnames(styles.search, {
                        [styles.searchTransparent]: this.props.transparent
                      })}
                      onClick={this.props.onSearchClicked}
                      data-testid="search-label"
                    >
                      <SearchIcon className={styles.searchIcon} />
                      <span className={styles.searchLabel}>Search</span>
                    </div>
                  )}
                </Box.Item>
              </Box>
            ) : (
              <div className={styles.navEmptyOverflow}>
                <div className={styles.navEmptyContainer}>
                  {departments &&
                    departments
                      .filter(department => department.launched)
                      .map(({ slug, displayName }) => {
                        return (
                          <Link key={slug} to={`/d/${slug}`} className={styles.navItemLink}>
                            {displayName}
                          </Link>
                        )
                      })}
                </div>
              </div>
            )}
          </nav>
        </GridItem>
      </Grid>
    )
  }
}

export default SecondaryNav
